import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Modal, Tooltip } from "antd";
import {
  DeleteOutlined,
  GlobalOutlined,
  TagOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLanguages } from "../../../../hooks/useStations";
import { useAppSelector } from "../../../../store/hooks";
import { useViewport } from "../../../../hooks/useViewport";
import { fmtEuro } from "../../../../utils/currency";
import { getItemUnitAndSize } from "../../../../utils/translations";
import {
  DangerButton,
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  posModalStyles as modalStyles,
} from "../../components/modalPrimitives";
import {
  isValidLunchDailyLimit,
  isValidLunchPrice,
  MAX_LUNCH_DAILY_LIMIT,
  MAX_LUNCH_PRICE,
  type LunchDraftItem,
} from "../lunchDraft";
import type { LunchProductView } from "../useLunchProducts";

interface LunchItemModalProps {
  draftItem: LunchDraftItem | null;
  product: LunchProductView | null;
  onClose: () => void;
  onApply: (draftItem: LunchDraftItem) => void;
  onRemove: (itemId: number) => void;
}

// ВРЕМЕННО СКРИТО: „Единица и размер" по езици (само без склад) не се показва
// в модала — сменя се само цената. Логиката остава непокътната (черновата,
// записът, BE, кухненският бон, мониторите); вече записаният размер се пази.
// За да се върне полето — `true`. Флаг вместо закоментиран JSX: коментарът би
// оставил неизползвани импорти/функции и `tsc`/eslint биха гръмнали.
const LUNCH_SIZE_EDITABLE = false;

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.08em] text-pos-dim">
    {children}
  </span>
);

/** Цена и „Единица и размер" по езици на продукт в обедното меню. */
export const LunchItemModal = ({
  draftItem,
  product,
  onClose,
  onApply,
  onRemove,
}: LunchItemModalProps) => {
  const { isMobile, isNarrow } = useViewport();
  const open = draftItem != null && product != null;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isMobile ? undefined : 580}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      {draftItem && product && (
        <LunchItemForm
          draftItem={draftItem}
          product={product}
          isNarrow={isNarrow}
          onClose={onClose}
          onApply={onApply}
          onRemove={onRemove}
        />
      )}
    </Modal>
  );
};

const LunchItemForm = ({
  draftItem,
  product,
  isNarrow,
  onClose,
  onApply,
  onRemove,
}: {
  draftItem: LunchDraftItem;
  product: LunchProductView;
  isNarrow: boolean;
  onClose: () => void;
  onApply: (draftItem: LunchDraftItem) => void;
  onRemove: (itemId: number) => void;
}) => {
  const { t } = useTranslation();
  const { isDesktop } = useViewport();
  const { data: languages = [] } = useLanguages();
  // Със склад количеството е на рецептата (складът изписва нея) — обедното
  // меню сменя САМО цената. Без склад размерът е само текст и се сменя.
  const usesWarehouse = useAppSelector(
    (state) => state.auth.user?.uses_warehouse === true,
  );
  const [price, setPrice] = useState(draftItem.price);
  const [unitLangs, setUnitLangs] = useState<Record<string, string>>(
    draftItem.unitAndSizeLangs,
  );
  // Порции на ден — текст, докато се пише; 0 = без ограничение
  const [dailyLimit, setDailyLimit] = useState(String(draftItem.dailyLimit ?? 0));
  // Курсорът е в цената, а тя е маркирана — пише се направо новата. antd Modal
  // взима фокуса при отваряне, затова го връщаме след анимацията (autoFocus не стига).
  const priceInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      priceInputRef.current?.focus();
      priceInputRef.current?.select();
    }, 120);
    return () => window.clearTimeout(timer);
  }, []);

  // Българският е първи; без заредени езици — поне той
  const languageCodes = useMemo(() => {
    const codes = languages.map((language) => language.code);
    return ["bg", ...codes.filter((code) => code !== "bg")];
  }, [languages]);

  const parsedPrice = Number(price.replace(",", "."));
  const priceValid = isValidLunchPrice(price);
  const priceTooHigh =
    Number.isFinite(parsedPrice) && parsedPrice > MAX_LUNCH_PRICE;
  const priceDiff =
    priceValid && product.mainPrice != null
      ? parsedPrice - product.mainPrice
      : 0;

  const dailyLimitValid = isValidLunchDailyLimit(dailyLimit);
  const canApply = priceValid && dailyLimitValid;

  const apply = () => {
    if (!canApply) return;
    onApply({
      itemId: draftItem.itemId,
      price: parsedPrice.toFixed(2),
      dailyLimit: Number(dailyLimit.trim()),
      unitAndSizeLangs: usesWarehouse
        ? {}
        : Object.fromEntries(
            Object.entries(unitLangs).filter(([, text]) => text.trim()),
          ),
    });
  };

  const subtitle = [
    product.mainPrice != null
      ? t("lunchMenu.mainPriceSubtitle", { price: fmtEuro(product.mainPrice) })
      : null,
    product.mainUnit,
  ]
    .filter(Boolean)
    .join(" · ");

  const removeButton = (
    <DangerButton
      icon={<DeleteOutlined />}
      label={t("lunchMenu.remove")}
      onClick={() => onRemove(draftItem.itemId)}
    />
  );
  const cancelButton = (
    <GhostButton
      label={t("common.cancel")}
      onClick={onClose}
      shortcut={isDesktop ? "Esc" : undefined}
    />
  );
  const applyButton = (
    <PrimaryButton
      label={t("lunchMenu.apply")}
      onClick={apply}
      disabled={!canApply}
      shortcut="⏎"
    />
  );

  return (
    <>
      <ModalHeader
        icon={<TagOutlined />}
        title={product.name}
        subtitle={subtitle}
        onClose={onClose}
        isNarrow={isNarrow}
      />
      <div
        className={`flex flex-col gap-5 ${isNarrow ? "px-4 py-4" : "px-[22px] py-5"}`}
      >
        <div className="flex flex-col items-center gap-2.5 rounded-[14px] bg-pos-panel px-4 py-4">
          <FieldLabel>{t("lunchMenu.lunchPrice")}</FieldLabel>
          <div className="flex h-[64px] w-[260px] max-w-full items-center gap-1 rounded-xl border border-pos-ctl bg-pos-card pl-4 pr-2 focus-within:border-pos-accent focus-within:shadow-[0_0_0_3px_rgba(201,124,44,.15)]">
            {/* Празна клетка отляво с ширината на бутона вдясно — числото
                остава в центъра на полето и с бутон, и без него */}
            <span aria-hidden className="w-9 shrink-0" />
            <input
              ref={priceInputRef}
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              onFocus={(event) => event.target.select()}
              onKeyDown={(event) => event.key === "Enter" && apply()}
              inputMode="decimal"
              aria-label={t("lunchMenu.lunchPrice")}
              className="w-full min-w-0 border-0 bg-transparent text-center font-pos-mono text-[28px] font-bold text-pos-ink outline-none"
            />
            <span className="font-pos-mono text-lg text-pos-soft2">€</span>
            {product.mainPrice != null && priceDiff !== 0 ? (
              <Tooltip title={t("lunchMenu.resetPrice")}>
                <button
                  type="button"
                  onClick={() => setPrice(product.mainPrice!.toFixed(2))}
                  aria-label={t("lunchMenu.resetPrice")}
                  className="grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-lg border-0 bg-transparent text-pos-dim transition-colors hover:bg-pos-stepper hover:text-pos-accent-ink"
                >
                  <UndoOutlined className="text-[15px]" />
                </button>
              </Tooltip>
            ) : (
              <span aria-hidden className="w-9 shrink-0" />
            )}
          </div>
          {priceTooHigh && (
            <span
              role="alert"
              className="text-[13px] font-semibold text-pos-danger-ink"
            >
              {t("lunchMenu.priceTooHigh", { max: fmtEuro(MAX_LUNCH_PRICE) })}
            </span>
          )}
          {product.mainPrice != null && (
            <div className="flex items-center gap-2 font-pos-mono text-[13px] text-pos-soft2">
              <span>
                {t("lunchMenu.mainPriceShort")}{" "}
                <span className={priceDiff !== 0 ? "line-through" : ""}>
                  {fmtEuro(product.mainPrice)}
                </span>
              </span>
              {priceValid && priceDiff !== 0 && (
                <span className="rounded-md bg-pos-disc-bg px-1.5 py-0.5 text-[12px] font-semibold text-pos-disc-ink">
                  {priceDiff > 0 ? "+" : "−"}
                  {fmtEuro(Math.abs(priceDiff))}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Порции на ден — центрирано като цената */}
        <div className="flex flex-col items-center gap-2 rounded-[14px] bg-pos-panel px-4 py-4">
          <FieldLabel>{t("lunchMenu.dailyLimitTitle")}</FieldLabel>
          <input
            value={dailyLimit}
            onChange={(event) => setDailyLimit(event.target.value)}
            onFocus={(event) => event.target.select()}
            onKeyDown={(event) => event.key === "Enter" && apply()}
            inputMode="numeric"
            maxLength={4}
            aria-label={t("lunchMenu.dailyLimitTitle")}
            aria-invalid={!dailyLimitValid}
            className={`h-[52px] w-[140px] max-w-full rounded-xl border bg-pos-card px-3 text-center font-pos-mono text-[22px] font-bold text-pos-ink outline-none focus:border-pos-accent ${
              dailyLimitValid ? "border-pos-ctl" : "border-pos-danger-ink"
            }`}
          />
          {dailyLimitValid ? (
            <span className="text-center text-[12.5px] text-pos-soft2">
              {t("lunchMenu.dailyLimitHint")}
            </span>
          ) : (
            <span role="alert" className="text-center text-[13px] font-semibold text-pos-danger-ink">
              {t("lunchMenu.dailyLimitInvalid", { max: MAX_LUNCH_DAILY_LIMIT })}
            </span>
          )}
        </div>

        {usesWarehouse ? (
          <span className="text-[12.5px] text-pos-soft2">
            {t("lunchMenu.recipeSizeFixed")}
          </span>
        ) : !LUNCH_SIZE_EDITABLE ? null : (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="flex-1">
                <FieldLabel>{t("lunchMenu.sizeTitle")}</FieldLabel>
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-pos-dim">
                <GlobalOutlined />
                {t("lunchMenu.byLanguage")}
              </span>
            </div>
            <div className="grid grid-cols-[44px_minmax(0,1fr)] items-center gap-x-2.5 gap-y-2">
              {languageCodes.map((code) => {
                const productUnit = product.item
                  ? getItemUnitAndSize(product.item.itemLangs, code)
                  : null;
                return (
                  <LanguageRow
                    key={code}
                    code={code}
                    value={unitLangs[code] ?? ""}
                    productUnit={productUnit}
                    onChange={(text) =>
                      setUnitLangs((prev) => ({ ...prev, [code]: text }))
                    }
                  />
                );
              })}
            </div>
            <span className="text-[12.5px] text-pos-soft2">
              {t("lunchMenu.sizeScopeHint")}
            </span>
          </div>
        )}
      </div>
      {/* На телефон ModalFooter слага ПЪРВИЯ бутон сам на цял ред най-долу —
          там трябва да е „Приложи", не „Махни"; на широк екран редът е
          Махни · Отказ · Приложи */}
      <ModalFooter isNarrow={isNarrow} columns="1fr 1fr 1.4fr">
        {isNarrow ? applyButton : removeButton}
        {isNarrow ? removeButton : cancelButton}
        {isNarrow ? cancelButton : applyButton}
      </ModalFooter>
    </>
  );
};

const LanguageRow = ({
  code,
  value,
  productUnit,
  onChange,
}: {
  code: string;
  value: string;
  productUnit: string | null;
  onChange: (text: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <span className="font-pos-mono text-[13px] font-bold uppercase text-pos-ink">
        {code}
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          maxLength={64}
          placeholder={productUnit ?? ""}
          aria-label={`${t("lunchMenu.sizeTitle")} ${code}`}
          className="h-11 rounded-[10px] border border-pos-ctl bg-pos-card px-3 font-pos-mono text-base text-pos-ink outline-none placeholder:text-pos-muted focus:border-pos-accent sm:text-[15px]"
        />
        <span className="font-pos-mono text-[11px] text-pos-muted">
          {productUnit
            ? t("lunchMenu.inAssortment", { value: productUnit })
            : t("lunchMenu.fromAssortment")}
        </span>
      </div>
    </>
  );
};
